import React, { useState } from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  IntlShape,
} from 'react-intl';
import Connector, { PropsFromRedux } from './Connector';
import { redirectToPatientIDB } from 'src/utils/patientHelpers';
import AlertCard from 'src/components/Sidebars/components/AlertCard';
import {
  BaseCard,
  BaseCardHeader,
  COMMON_COLORS,
  Icon,
  SkeletonWrapper,
} from '@neteera/neteera-web-nextgen';
import AlertLogModal from 'src/routes/IndividualDashboard/components/DashboardTab/MainGraph/AlertLog/AlertLogModal';
import { deepEqual } from 'src/utils/comparators';
import dayjs from 'dayjs';

type Props = PropsFromRedux & {
  intl: IntlShape;
};

const NotificationsWidget = ({
  intl,
  patientNotifications,
  alertLogMetadata,
  clearAllPatientAlerts,
  isLoading,
  timeFrame,
}: Props): JSX.Element => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const {
    id = '',
    deviceId = '',
    patientId = '',
  } = patientNotifications[0] ?? {};
  const { startTime, endTime } = timeFrame;
  const hoursDifferences = dayjs(endTime)?.diff(dayjs(startTime), 'hours');
  return (
    <>
      <BaseCard className="flex flex-col">
        <BaseCardHeader
          title={intl.formatMessage(messages.notificationsLabel)}
          leftIconProps={{ iconName: 'Notification' }}
          size="base"
        >
          <Icon
            iconName={'Expand'}
            className={'cursor-pointer'}
            onClick={() => setIsModalVisible(true)}
          />
        </BaseCardHeader>
        <SkeletonWrapper full isLoading={isLoading}>
          {patientNotifications.length ? (
            <AlertCard
              alertId={id}
              deviceId={deviceId}
              patientId={patientId}
              config={{
                allowAlertDelete: true,
                displayPatientName: false,
                displaySiteLocation: false,
                displaySimilarNotifications: false,
              }}
              onPatientNameClick={() => redirectToPatientIDB(patientId)}
              onDeleteAlertClick={id => clearAllPatientAlerts(id)}
            />
          ) : (
            <FormattedMessage {...messages.noDataText} />
          )}
          <BaseCard
            background={COMMON_COLORS.teal100}
            className="px-4 py-2 border border-solid border-grey-200 shadow-none"
          >
            <SkeletonWrapper full isLoading={isLoading}>
              <FormattedMessage
                {...messages.notificationsInTimeFrameSelection}
                values={{
                  alertCounters: alertLogMetadata.totalResults || 0,
                  timeframe:
                    hoursDifferences > 72
                      ? Math.ceil(hoursDifferences / 24)
                      : hoursDifferences,
                  timeUnit:
                    hoursDifferences > 72
                      ? intl.formatMessage(messages.days)
                      : intl.formatMessage(messages.hours),
                }}
              />
            </SkeletonWrapper>
          </BaseCard>
        </SkeletonWrapper>
      </BaseCard>
      <AlertLogModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        isLoading={isLoading}
        startDate={startTime}
        endDate={endTime}
      />
    </>
  );
};

const messages = defineMessages({
  notificationsLabel: {
    id: 'NotificationsWidget.index.notificationsLabel',
    defaultMessage: 'Notifications',
  },
  hours: {
    id: 'NotificationsWidget.index.hours',
    defaultMessage: 'hours',
  },
  days: {
    id: 'NotificationsWidget.index.days',
    defaultMessage: 'days',
  },
  noDataText: {
    id: 'NotificationsWidget.index.noDataText',
    defaultMessage: 'No active notifications',
  },
  notificationsInTimeFrameSelection: {
    id: 'NotificationsWidget.index.notificationsInTimeFrameSelection',
    defaultMessage:
      '{alertCounters} notifications in the last {timeframe} {timeUnit}',
  },
});

export default Connector(
  injectIntl(
    React.memo(NotificationsWidget, (oldProps, newProps) =>
      deepEqual(oldProps, newProps),
    ),
  ),
);
